import { useMemo } from 'react'
import { Info, TriangleAlert } from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent } from '../../lib/shadcn/tooltip'
import { cn } from '../../lib/shadcn/utils'
import {
  type StatementLine,
  sectionLabel,
  sectionRank,
  formatZar,
} from '../../utils/statements'

function AmountCell({ line }: { line: StatementLine }) {
  if (line.is_error) {
    return (
      <div className="flex items-center justify-end gap-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-1.5 italic text-muted-foreground">
              <TriangleAlert className="h-3.5 w-3.5 text-amber-500" />
              Not available
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            {line.error_note || 'This value could not be sourced for this period.'}
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }
  const negative = (line.amount_zar ?? 0) < 0
  return (
    <span
      className={cn(
        'tabular-nums',
        line.is_subtotal && 'font-bold',
        negative && 'text-rose-600 dark:text-rose-400',
      )}
    >
      {formatZar(line.amount_zar)}
    </span>
  )
}

function LineRow({ line }: { line: StatementLine }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-1.5 text-sm',
        line.is_subtotal && 'border-t border-border bg-muted/40',
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <span
          className={cn(
            'truncate text-foreground',
            line.is_subtotal ? 'font-bold' : line.is_error && 'text-muted-foreground',
          )}
        >
          {line.label}
        </span>
        {line.description && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label={`About ${line.label}`} className="shrink-0">
                <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{line.description}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className="shrink-0 text-right">
        <AmountCell line={line} />
      </div>
    </div>
  )
}

export function StatementLines({ lines }: { lines: StatementLine[] }) {
  const grouped = useMemo(() => {
    const map = new Map<string, StatementLine[]>()
    for (const l of lines) {
      const arr = map.get(l.section) ?? []
      arr.push(l)
      map.set(l.section, arr)
    }
    const sections = Array.from(map.keys()).sort((a, b) => sectionRank(a) - sectionRank(b))
    return sections.map((code) => ({
      code,
      label: sectionLabel(code),
      lines: (map.get(code) ?? []).sort((a, b) => a.sort_order - b.sort_order),
    }))
  }, [lines])

  if (lines.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No line items for this period.
      </div>
    )
  }

  return (
    <div className="divide-y divide-border rounded-lg border border-border bg-card">
      {grouped.map((section) => (
        <div key={section.code}>
          <div className="bg-muted/60 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {section.label}
          </div>
          <div>
            {section.lines.map((line) => (
              <LineRow key={line.line_code || `${line.section}-${line.sort_order}`} line={line} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
